import { Injectable, Injector, OnDestroy } from '@angular/core'
import {
  Analytics,
  isSupported,
  setUserId as setFirebaseUserId,
  setUserProperties as setFirebaseUserProperties,
} from '@angular/fire/analytics'
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics'
import { sampleTime, Subject, takeUntil } from 'rxjs'
import * as StackTrace from 'stacktrace-js'

import { Capacitor } from '@capacitor/core'
import { CookieVendorId } from '../cookie-consent/cookie-consent.fixtures'
import { CookieConsentService } from '../cookie-consent/cookie-consent.service'
import { FacebookEventService } from '../facebook-event/facebook-event.service'
import { EventName, EventValue, UserProperty } from './analytics.model'
import { environment } from '../../../environments/environment'
import { GtmService } from '../gtm/gtm.service'
import { Router } from '@angular/router'
import { ScrollCustomEvent } from '@ionic/angular'

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()
  private production: boolean = environment.production
  private canLogGoogleAnalytics: boolean = false
  private canLogMeta: boolean = false
  private canLogGoogleAds: boolean = false
  private isCapacitor: boolean = false
  private analytics: Analytics = undefined

  private get anyWindow(): any {
    return window as any
  }

  constructor(
    private cookieConsentService: CookieConsentService,
    private facebookEventService: FacebookEventService,
    private router: Router,
    private gtmService: GtmService,
    private injector: Injector
  ) {
    this.isCapacitor = Capacitor.isNativePlatform()
    this.cookieConsentService.userPreferences
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(async (userPreferences) => {
        const canTrack = cookieConsentService.canTrack.value
        this.canLogGoogleAnalytics =
          userPreferences[CookieVendorId.googleAnalytics]
        this.canLogMeta = userPreferences[CookieVendorId.meta] && canTrack
        this.canLogGoogleAds =
          userPreferences[CookieVendorId.googleAds] && canTrack

        this.updatePreferences()
        this.handleUnsentReports()
        this.debugPermissions()
      })

    isSupported().then((supported: boolean) => {
      if (supported) {
        this.analytics = this.injector.get(Analytics)
      }
    })

    this.scrollPos.pipe(sampleTime(2000)).subscribe((scrollState) => {
      this.logEvent('scroll_update', {
        scrollLeft: scrollState.scrollLeft,
        scrollTop: scrollState.scrollTop,
        scrollTarget: scrollState.target,
      })
    })
  }

  ngOnDestroy() {
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  private last_user_id_set?: string = undefined

  async setUserId(userId: string) {
    if (userId === this.last_user_id_set) {
      return
    }
    this.last_user_id_set = userId

    if (this.canLogGoogleAnalytics) {
      if (this.isCapacitor) {
        await FirebaseAnalytics.setUserId({
          userId,
        })
      } else {
        setFirebaseUserId(this.analytics, userId)
        this.logEvent('user_id_set', { user_id: userId })
      }
    }

    if (this.canLogGoogleAnalytics && this.isCapacitor) {
      await FirebaseCrashlytics.setUserId({
        userId,
      })
    }
  }

  private scrollPos = new Subject<{
    scrollTop: number
    scrollLeft: number
    target: string
  }>()
  public ionScrollEvent(target: string, event: ScrollCustomEvent) {
    this.scrollPos.next({
      scrollLeft: event.detail.scrollLeft,
      scrollTop: event.detail.scrollTop,
      target,
    })
  }
  public logScrollsFor(target: string, element: HTMLElement) {
    if (!element) {
      return
    }
    element.onscrollend = () =>
      this.scrollPos.next({
        scrollLeft: element.scrollLeft,
        scrollTop: element.scrollTop,
        target,
      })
  }

  async setUserLanguage(language: string) {
    if (this.canLogGoogleAnalytics) {
      if (this.isCapacitor) {
        await FirebaseAnalytics.setUserProperty({
          key: UserProperty.language,
          value: language,
        })
      } else {
        this.logEvent('user_language_set', { language: language })
        setFirebaseUserProperties(this.analytics, {
          key: UserProperty.language,
          value: language,
        })
      }
    }
  }

  setCurrentScreen(name: string, depth: number) {
    this.logEvent('page_view', {
      name,
      depth,
      url: this.router.routerState.snapshot.url,
    })
  }

  logEvent(eventName: string, eventValues: { [key: string]: any } = undefined) {
    eventValues = eventValues ?? {}
    eventValues['testing'] = !environment.production
    eventValues['platform'] = Capacitor.getPlatform()

    this.debugEvent(eventName, eventValues)

    if (this.isCapacitor) {
      if (this.canLogMeta) {
        this.facebookEventService.logEvent(eventName, eventValues).then()
      }
      if (this.canLogGoogleAnalytics) {
        FirebaseAnalytics.logEvent({
          params: eventValues,
          name: eventName,
        }).then()
      }
    } else {
      this.gtmService.pushTag(eventName, eventValues)
    }
  }

  async logLogin() {
    this.logEvent(EventName.login)
  }

  async logSignup() {
    this.logEvent(EventName.signUpStarted)
  }

  async logWizardStep(step: number) {
    const event = `${EventName.wizardStep}${step}`
    this.logEvent(event)
  }

  async logWizardFinish() {
    this.logEvent(EventName.wizardSend)
  }

  async logMatch() {
    this.logEvent(EventName.matchGenerated)
  }

  async logChatOpen() {
    this.logEvent(EventName.chatOpen)
  }

  async logApplicationLinkClick() {
    this.logEvent(EventName.applicationLinkClick)
  }

  async logAppStoreClick(store: 'google' | 'apple') {
    this.logEvent(EventName.appStoreClick, { [EventValue.store]: store })
  }

  // Crashlytics

  didCrashOnPreviousExecution = async () => {
    if (!this.isCapacitor) {
      return undefined
    }
    const { crashed } = await FirebaseCrashlytics.didCrashOnPreviousExecution()
    return crashed
  }

  handleUnsentReports = async () => {
    if (!this.isCapacitor) {
      return
    }
    if (this.canLogGoogleAnalytics) {
      await FirebaseCrashlytics.sendUnsentReports()
    } else {
      await FirebaseCrashlytics.deleteUnsentReports()
    }
  }

  recordException = async () => {
    if (!this.isCapacitor) {
      return
    }
    if (!this.canLogGoogleAnalytics) {
      return
    }
    await FirebaseCrashlytics.recordException({
      message: 'This is a non-fatal message.',
    })
  }

  recordExceptionWithStacktrace = async (error: Error) => {
    if (!this.isCapacitor) {
      return
    }
    const stacktrace = await StackTrace.fromError(error)
    await FirebaseCrashlytics.recordException({
      message: 'This is a non-fatal message.',
      stacktrace,
    })
  }

  async updatePreferences() {
    if (!this.isCapacitor) {
      return
    }
    await FirebaseAnalytics.setEnabled({ enabled: this.canLogGoogleAnalytics })
    await FirebaseCrashlytics.setEnabled({
      enabled: this.canLogGoogleAnalytics,
    })
  }

  private debugEvent(
    eventName: string,
    eventValues: { [key: string]: any } = undefined
  ) {
    console.log(
      `[AnalyticsService]: Event ${eventName}: ${JSON.stringify(eventValues)}`
    )
    this.debugPermissions()
  }

  private debugPermissions() {
    if (this.production) {
      return
    }
    console.log(`[AnalyticsService]: canLogGoogleAds = ${this.canLogGoogleAds}`)
    console.log(
      `[AnalyticsService]: canLogGoogleAnalytics = ${this.canLogGoogleAnalytics}`
    )
    console.log(`[AnalyticsService]: canLogMeta = ${this.canLogMeta}`)
  }
}
